import React, { useEffect, useState, useMemo } from 'react';
import PropTypes from 'prop-types';
import { useDispatch } from 'react-redux';

import {
  answerBlock
} from 'actions';

import Question from 'components/Questionnaires/Question';
import { QuestionTextField, QuestionJurisdictionDropDownField } from 'components/Questionnaires/fields';
import { requiredValidator, validateQuestion } from 'components/Questionnaires/validators';

export const QuestionAddressBlock = ({ block, onAnswer }) => {
  const dispatch = useDispatch();
  const [answer, setAnswer] = useState(block?.answers ?? {});

  const [line1, setLine1] = useState({
    id: 'line1',
    component: QuestionTextField,
    label: block?.componentProps?.line1Label ?? 'questionnaire.address.1.label',
    info: {
      footnote: block?.componentProps?.line1Footnote ?? 'questionnaire.address.1.footnote'
    },
    visible: true,
    disabled: block.disabled ?? undefined,
    validation: block?.componentProps?.optional !== true ? {
      conditions: [
        { condition: requiredValidator, message: 'questionnaire.address.1.errorMessage' }
      ]
    } : undefined,
    answer: answer?.line1
  });
  const line2 = { id: 'line2', component: QuestionTextField, label: 'questionnaire.address.2.label', visible: true, disabled: block.disabled ?? undefined, answer: answer?.line2 };
  const line3 = { id: 'line3', component: QuestionTextField, label: 'questionnaire.address.3.label', className: 'col-6', visible: true, disabled: block.disabled ?? undefined, answer: answer?.line3 };
  const line4 = { id: 'line4', component: QuestionTextField, label: 'questionnaire.address.4.label', className: 'col-6', visible: true, disabled: block.disabled ?? undefined, answer: answer?.line4 };
  const postalCode = { id: 'postalCode', component: QuestionTextField, className: 'col-4', label: 'questionnaire.address.postCode.label', visible: true, disabled: block.disabled ?? undefined, answer: answer?.postalCode };
  const [country, setCountry] = useState({
    id: 'country',
    component: QuestionJurisdictionDropDownField,
    label: 'questionnaire.address.country.label',
    className: 'col-8',
    visible: true,
    disabled: block.disabled ?? undefined,
    validation: block?.componentProps?.optional !== true ? {
      conditions: [
        { condition: requiredValidator, message: 'questionnaire.address.country.errorMessage' }
      ]
    } : undefined,
    answer: answer?.country
  });

  const blockIdentifier = useMemo(() => ((block && block.id) ? `${ block.id }${ block.index !== undefined ? (`.${ block.index }`) : '' }` : undefined), [block.index]);

  const isEmptyObject = (obj) => {
    if (!obj) {
      return true;
    }
    let isEmpty = true;
    Object.keys(obj).forEach((key) => {
      if (obj[key]) {
        isEmpty = false;
      }
    });
    return isEmpty;
  };

  const isValid = () => {
    if (block.linkBlock && (!answer || isEmptyObject(answer))) {
      return true;
    }

    if (block.componentProps?.optional === true) {
      const linesEntered = [
        answer?.line1,
        answer?.line2,
        answer?.line3,
        answer?.line4,
        answer?.postalCode,
        answer?.country,
      ].some(line => line?.length > 0);
      return (!linesEntered || (line1.isValid && country.isValid));
    }
    return (line1.isValid && country.isValid);
  };

  const getRequiredValidator = message => ({
    conditions: [
      { condition: requiredValidator, message }
    ]
  });

  const updateValidators = (q) => {
    if (block.componentProps?.optional !== true) {
      return;
    }

    let line1Entered = answer?.line1?.length > 0;
    let countryEntered = answer?.country?.length > 0;
    let optionaLinesEntered = answer?.line2?.length > 0 || answer?.line3?.length > 0 || answer?.line4?.length > 0 || answer?.postalCode?.length > 0;

    switch (q?.id) {
      case 'line1':
        line1Entered = q.answer?.length > 0;
        break;
      case 'line2':
        optionaLinesEntered = q.answer?.length > 0 || answer?.line3?.length > 0 || answer?.line4?.length > 0 || answer?.postalCode?.length > 0;
        break;
      case 'line3':
        optionaLinesEntered = answer?.line2?.length > 0 || q.answer?.length > 0 || answer?.line4?.length > 0 || answer?.postalCode?.length > 0;
        break;
      case 'line4':
        optionaLinesEntered = answer?.line2?.length > 0 || answer?.line3?.length > 0 || q.answer?.length > 0 || answer?.postalCode?.length > 0;
        break;
      case 'postalCode':
        optionaLinesEntered = answer?.line2?.length > 0 || answer?.line3?.length > 0 || answer?.line4?.length > 0 || q.answer?.length > 0;
        break;
      case 'country':
        countryEntered = q.answer?.length > 0;
        break;
      default:
        break;
    }

    const newCountry = { ...country };
    const newLine1 = { ...line1 };

    if (line1Entered || countryEntered || optionaLinesEntered) {
      newLine1.validation = newLine1.validation ?? getRequiredValidator('questionnaire.address.1.errorMessage');
      newCountry.validation = newCountry.validation ?? getRequiredValidator('questionnaire.address.country.errorMessage');
    } else {
      newLine1.validation = undefined;
      newLine1.isValid = undefined;
      newLine1.errorMessages = undefined;
      newCountry.validation = undefined;
      newCountry.isValid = undefined;
      newCountry.errorMessages = undefined;
    }

    switch (q?.id) {
      case 'line1':
        q.validation = newLine1.validation;
        q.isValid = newLine1.isValid;
        q.errorMessages = newLine1.errorMessages;
        setCountry(newCountry);
        break;
      case 'country':
        q.validation = newCountry.validation;
        q.isValid = newCountry.isValid;
        q.errorMessages = newCountry.errorMessages;
        setLine1(newLine1);
        break;
      default:
        setLine1(newLine1);
        setCountry(newCountry);
        break;
    }
  };

  const onQuestionChange = (q, value) => {
    switch (q.id) {
      case 'line1': {
        const newLine1 = { ...q, answer: value };
        updateValidators(newLine1);
        validateQuestion(newLine1);
        setLine1(newLine1);
        setAnswer({ ...answer, line1: value });
        break;
      }
      case 'line2': {
        const newLine2 = { ...q, answer: value };
        updateValidators(newLine2);
        setAnswer({ ...answer, line2: value });
        break;
      }
      case 'line3': {
        const newLine3 = { ...q, answer: value };
        updateValidators(newLine3);
        setAnswer({ ...answer, line3: value });
        break;
      }
      case 'line4': {
        const newLine4 = { ...q, answer: value };
        updateValidators(newLine4);
        setAnswer({ ...answer, line4: value });
        break;
      }
      case 'postalCode': {
        const newPostalCode = { ...q, answer: value };
        updateValidators(newPostalCode);
        setAnswer({ ...answer, postalCode: value });
        break;
      }
      case 'country': {
        const newCountry = { ...q, answer: value };
        updateValidators(newCountry);
        validateQuestion(newCountry);
        setCountry(newCountry);
        setAnswer({ ...answer, country: value });
        break;
      }
      default:
        break;
    }
  };

  useEffect(() => {
    if (!onAnswer || typeof onAnswer !== 'function') {
      if (Object.keys(answer).length > 0 && answer.constructor === Object) {
        if (line1.answer) {
          validateQuestion(line1);
          setLine1(line1);
        }
        if (country.answer) {
          validateQuestion(country);
          setCountry(country);
        }
        dispatch(answerBlock(block.id, answer, isValid()));
      } else {
        onQuestionChange(line1, undefined);
        onQuestionChange(country, undefined);
      }
    } else {
      onAnswer(block, answer, isValid());
    }
  }, [answer]);

  useEffect(() => {
    if (JSON.stringify(answer) === JSON.stringify(block.answers)) {
      return;
    }
    if (line1.answer !== block.answers?.line1) {
      const newLine1 = { ...line1, answer: block.answers?.line1 };
      if (block.answers?.line1 === undefined) {
        newLine1.isValid = undefined;
        newLine1.isForceEdited = false;
        newLine1.validation = undefined;
        newLine1.errorMessages = undefined;
      }
      setLine1(newLine1);
    }
    if (country.answer !== block.answers?.country) {
      const newCountry = { ...country, answer: block.answers?.country };
      if (block.answers?.country === undefined) {
        newCountry.isValid = undefined;
        newCountry.isForceEdited = false;
        newCountry.validation = undefined;
        newCountry.errorMessages = undefined;
      }
      setCountry(newCountry);
    }
    setAnswer(block.answers ?? {});
  }, [block.answers]);

  const onBlockForceEdit = () => {
    const newLine1 = { ...line1, isForceEdited: true };
    if (!answer?.line1) {
      newLine1.answer = '';
    }
    updateValidators(newLine1);
    validateQuestion(newLine1);

    const newCountry = { ...country, isForceEdited: true };
    if (!answer?.country) {
      newCountry.answer = '';
    }
    updateValidators(newCountry);
    validateQuestion(newCountry);

    setLine1(newLine1);
    setCountry(newCountry);
    setAnswer({ ...answer, line1: newLine1.answer, country: newCountry.answer });
  };

  useEffect(() => {
    setLine1({
      ...line1,
      validation: block?.componentProps?.optional !== true ? { conditions: [{ condition: requiredValidator, message: 'questionnaire.address.1.errorMessage' }] } : undefined,
      isValid: undefined,
      errorMessages: undefined,
      isForceEdited: false,
      answer: block.answers?.line1
    });
    setCountry({
      ...country,
      validation: block?.componentProps?.optional !== true ? { conditions: [{ condition: requiredValidator, message: 'questionnaire.address.country.errorMessage' }] } : undefined,
      isValid: undefined,
      errorMessages: undefined,
      isForceEdited: false,
      answer: block.answers?.country
    });
  }, [block?.componentProps?.optional]);

  useEffect(() => {
    if (block.isForceEdited === true) {
      onBlockForceEdit();
    }
  }, [block.isForceEdited]);

  return (<React.Fragment>
    <Question key={ line1.id } blockId={ blockIdentifier } question={ line1 } { ...line1.componentProps } onChange={ (q, a) => onQuestionChange(q, a) } />
    <Question key={ line2.id } blockId={ blockIdentifier } question={ line2 } { ...line2.componentProps } onChange={ (q, a) => onQuestionChange(q, a) } />
    <Question key={ line3.id } blockId={ blockIdentifier } question={ line3 } { ...line3.componentProps } onChange={ (q, a) => onQuestionChange(q, a) } />
    <Question key={ line4.id } blockId={ blockIdentifier } question={ line4 } { ...line4.componentProps } onChange={ (q, a) => onQuestionChange(q, a) } />
    <Question key={ postalCode.id } blockId={ blockIdentifier } question={ postalCode } { ...postalCode.componentProps } onChange={ (q, a) => onQuestionChange(q, a) } />
    <Question key={ country.id } blockId={ blockIdentifier } question={ country } { ...country.componentProps } onChange={ (q, a) => onQuestionChange(q, a) } />
  </React.Fragment>);
};

QuestionAddressBlock.displayName = 'QuestionAddressBlock';

QuestionAddressBlock.propTypes = {
  block: PropTypes.shape().isRequired,
  onAnswer: PropTypes.func,
};

QuestionAddressBlock.defaultProps = {
  onAnswer: null,
};

export default QuestionAddressBlock;
